import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";
import { useAuthStore } from '../auth/auth-store';

function getIncidentFilter() {
  const inc = useAuthStore.getState().activeIncident;
  return inc ? [{ field: FIELDS.msOrganizations.incidentId, operator: 'eq' as const, value: inc.id }] : [];
}

const FMO = FIELDS.msOrganizations;
const FMF = FIELDS.msFacilities;
const FML = FIELDS.msOrgLists;
const FMP = FIELDS.msReentryPasses;
const FMC = FIELDS.msContacts;

function mapMSOrg(r: Record<string, unknown>): MSOorg {
  const orgName = (r[FMO.orgName] as string) || '';
  const contactName = (r[FMO.contactName] as string) || '';
  const sector = (r[FMO.sector] as string) || '';
  const lat = r[FMO.latitude] ? Number(r[FMO.latitude]) : undefined;
  const lng = r[FMO.longitude] ? Number(r[FMO.longitude]) : undefined;
  return {
    id: r.record_id as number,
    orgName,
    name: orgName,
    opStatus: (r[FMO.opStatus] as string) || '',
    sector,
    sectorType: sector,
    contactName,
    contactPhone: (r[FMO.contactPhone] as string) || '',
    contactEmail: (r[FMO.contactEmail] as string) || '',
    ownerEmail: (r[FMO.ownerEmail] as string) || '',
    employeeCount: String(r[FMO.employeeCount] || ''),
    primaryContact: contactName,
    address: (r[FMO.address] as string) || '',
    city: (r[FMO.city] as string) || '',
    state: (r[FMO.state] as string) || '',
    zip: (r[FMO.zip] as string) || '',
    notes: (r[FMO.notes] as string) || '',
    // FMO.coordinates is a stub key ('') — derive the "lat,lng" string from actual lat/lng fields
    coordinates: (lat && lng) ? `${lat},${lng}` : '',
    email: (r[FMO.contactEmail] as string) || (r[FMO.ownerEmail] as string) || '',
    phone: (r[FMO.contactPhone] as string) || '',
    lastVerified: (r[FMO.lastVerified] as string) || '',
    incidentId: r[FMO.incidentId] ? Number(r[FMO.incidentId]) : null,
    latitude: lat,
    longitude: lng,
    directoryOrgId: FMO.directoryOrgId ? (r[FMO.directoryOrgId] as string) || '' : '',
  };
}
function mapMSPass(r: Record<string, unknown>): MSPass {
  return { id: r.record_id as number, authId: (r[FMP.authId] as number) || 0, orgId: (r[FMP.orgId] as number) || 0, orgName: (r[FMP.orgName] as string) || "", passCode: (r[FMP.passCode] as string) || "", status: (r[FMP.status] as string) || "issued", issuedAt: (r[FMP.issuedAt] as string) || "", expiresAt: (r[FMP.expiresAt] as string) || "" };
}
function mapMSContact(r: Record<string, unknown>): MSContact {
  const firstName = (r[FMC.firstName] as string) || '';
  const lastName = (r[FMC.lastName] as string) || '';
  return {
    id: r.record_id as number,
    orgId: (r[FMC.orgId] as number) || 0,
    orgId_s: String(r[FMC.orgId] || ''),
    firstName, lastName,
    name: [firstName, lastName].filter(Boolean).join(' ') || '',
    title: (r[FMC.title] as string) || '',
    phone: (r[FMC.phone] as string) || '',
    email: (r[FMC.email] as string) || '',
    role: (r[FMC.role] as string) || '',
    notes: (r[FMC.notes] as string) || '',
    dept: '', type: (r[FMC.role] as string) || '', altPhone: '',
    canAuth: (r[FMC.role] as string) === 'decision_maker' ? 'true' : 'false',
    personId: FMC.personId ? (r[FMC.personId] as string) || '' : '',
  };
}

interface MSOorg { id: number; orgName: string; name: string; opStatus: string; sector: string; sectorType: string; contactName: string; contactPhone: string; contactEmail: string; ownerEmail: string; employeeCount: string; primaryContact: string; address: string; city: string; state: string; zip: string; notes: string; coordinates: string; email: string; phone: string; lastVerified: string; incidentId: number | null; latitude?: number; longitude?: number; [k: string]: unknown; }
interface MSPass { id: number; authId: number; orgId: number; orgName: string; passCode: string; status: string; issuedAt: string; expiresAt: string; [k: string]: unknown; }
export interface MSContact { id: number; orgId: number | string; orgId_s?: string; firstName: string; lastName: string; name?: string; title: string; phone: string; altPhone?: string; email: string; role?: string; dept?: string; type?: string; canAuth?: string; notes?: string; personId?: string; [k: string]: unknown; }
export interface MSFacility {
  id: number; orgId: string; name: string; type: string;
  address: string; city: string; state: string; zip: string;
  buildingType: string; sqft: string; stories: string;
  occDay: string; occNight: string;
  hasGen: string; genDetails: string;
  hasHazmat: string; hazmatDetails: string;
  notes: string; coordinates: string; status: string;
  latitude?: number | null; longitude?: number | null;
  [k: string]: unknown;
}

const MS_PAGE_SIZE = 100;

interface MainStreetState {
  organizations: MSOorg[]; orgsTotal: number; orgsPage: number;
  passes: MSPass[]; contacts: MSContact[]; loading: boolean; loadingContacts: boolean;
  fetchOrganizations: () => Promise<void>; setOrgsPage: (page: number) => void;
  fetchPasses: () => Promise<void>; fetchContacts: () => Promise<void>;
  createOrganization: (data: Record<string, unknown>) => Promise<number | null>;
  createPass: (data: Record<string, unknown>) => Promise<number | null>;
  updatePass: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteOrganization: (id: number) => Promise<void>;
  updateOrganization: (id: number, data: Record<string, unknown>) => Promise<void>;
  // V1 compat
  loadOrganizations: () => Promise<void>;
  loadingOrgs: boolean;
  loadContacts: () => Promise<void>;
  createContact: (data: any) => Promise<number>;
  updateContact: (id: any, data: any) => Promise<void>;
  deleteContact: (id: any) => Promise<void>;
  facilities: MSFacility[];
  loadFacilities: () => Promise<void>;
  createFacility: (data: Partial<MSFacility>) => Promise<number>;
  updateFacility: (id: number, data: Partial<MSFacility>) => Promise<void>;
  deleteFacility: (id: number) => Promise<void>;
  loadingFacilities: boolean;
  orgLists: any[];
  loadingLists: boolean;
  loadOrgLists: () => Promise<void>;
  createOrgList: (data: any) => Promise<number>;
  updateOrgList: (id: any, data: any) => Promise<void>;
  deleteOrgList: (id: any) => Promise<void>;
  loadPasses: () => Promise<void>;
  geocodeUnmappedOrgs: (onProgress?: (done: number, total: number) => void) => Promise<number>;
}

export const useMainStreetStore = create<MainStreetState>((set, get) => ({
  organizations: [], orgsTotal: 0, orgsPage: 1,
  passes: [], contacts: [], facilities: [], loading: false, loadingFacilities: false, loadingContacts: false, loadingLists: false,
  fetchOrganizations: async () => {
    set({ loading: true });
    try {
      const { orgsPage } = get();
      const filters = getIncidentFilter();
      const r = filters.length
        ? await queryRecords<Record<string, unknown>>(TABLES.msOrganizations, {
            filters,
            limit: MS_PAGE_SIZE,
            offset: (orgsPage - 1) * MS_PAGE_SIZE,
            sort: { field: 'created_at', direction: 'desc' },
          })
        : await fetchRecords<Record<string, unknown>>(TABLES.msOrganizations, {
            limit: MS_PAGE_SIZE,
            offset: (orgsPage - 1) * MS_PAGE_SIZE,
            sort: { field: 'created_at', direction: 'desc' },
          });
      set({ organizations: r.records.map(mapMSOrg), orgsTotal: r.total });
    } catch (e) { console.error('[MS]', e); }
    set({ loading: false });
  },
  setOrgsPage: (page) => { set({ orgsPage: page }); get().fetchOrganizations(); },
  fetchPasses: async () => { try { const r = await fetchRecords<Record<string, unknown>>(TABLES.msReentryPasses, { limit: 500 }); set({ passes: r.records.map(mapMSPass) }); } catch (e) { console.error('[MS]', e); } },
  fetchContacts: async () => {
    set({ loadingContacts: true });
    try {
      const r = await fetchRecords<Record<string, unknown>>(TABLES.msContacts, { limit: 500 });
      set({ contacts: r.records.map(mapMSContact) });
    } catch (e) { console.error('[MS]', e); }
    finally { set({ loadingContacts: false }); }
  },
  createOrganization: async (data) => {
    const p: Record<string,unknown> = {};
    if (data.orgName) p[FMO.orgName] = data.orgName; if (data.opStatus) p[FMO.opStatus] = data.opStatus;
    if (data.sector) p[FMO.sector] = data.sector; if (data.contactName) p[FMO.contactName] = data.contactName;
    if (data.contactPhone) p[FMO.contactPhone] = data.contactPhone; if (data.address) p[FMO.address] = data.address;
    if (data.city) p[FMO.city] = data.city; if (data.contactEmail) p[FMO.contactEmail] = data.contactEmail;
    if (data.ownerEmail) p[FMO.ownerEmail] = data.ownerEmail;
    if (data.employeeCount !== undefined) p[FMO.employeeCount] = data.employeeCount;
    if (data.zip) p[FMO.zip] = data.zip;
    if (data.notes !== undefined) p[FMO.notes] = data.notes;
    if (data.incidentId) p[FMO.incidentId] = data.incidentId;
    if (FMO.directoryOrgId && data.directoryOrgId !== undefined) p[FMO.directoryOrgId] = data.directoryOrgId;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.msOrganizations, p); await get().fetchOrganizations(); return (r.record_id ?? r.id) as number; } catch (e) { console.error('[MS]', e); return null; }
  },
  createPass: async (data) => {
    const p: Record<string,unknown> = {};
    if (data.authId) p[FMP.authId] = data.authId; if (data.orgId) p[FMP.orgId] = data.orgId;
    if (data.orgName) p[FMP.orgName] = data.orgName; if (data.passCode) p[FMP.passCode] = data.passCode;
    if (data.status) p[FMP.status] = data.status; if (data.issuedAt) p[FMP.issuedAt] = data.issuedAt;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.msReentryPasses, p); await get().fetchPasses(); return (r.record_id ?? r.id) as number; } catch (e) { console.error('[MS]', e); return null; }
  },
  updatePass: async (id, data) => {
    const p: Record<string,unknown> = {};
    if (data.status) p[FMP.status] = data.status;
    try { await updateRecord(TABLES.msReentryPasses, id, p); await get().fetchPasses(); } catch (e) { console.error('[MS]', e); }
  },
  deleteOrganization: async (id) => {
    try { await deleteRecord(TABLES.msOrganizations, id); await get().fetchOrganizations(); } catch (e) { console.error('[MS]', e); }
  },
  updateOrganization: async (id, data) => {
    const p: Record<string,unknown> = {};
    if (data.orgName) p[FMO.orgName] = data.orgName; if (data.opStatus) p[FMO.opStatus] = data.opStatus;
    if (data.sector) p[FMO.sector] = data.sector; if (data.contactName) p[FMO.contactName] = data.contactName;
    if (data.contactPhone) p[FMO.contactPhone] = data.contactPhone; if (data.address) p[FMO.address] = data.address;
    if (data.city) p[FMO.city] = data.city;
    if (data.contactEmail !== undefined) p[FMO.contactEmail] = data.contactEmail;
    if (data.ownerEmail !== undefined) p[FMO.ownerEmail] = data.ownerEmail;
    if (data.employeeCount !== undefined) p[FMO.employeeCount] = data.employeeCount;
    if (data.zip !== undefined) p[FMO.zip] = data.zip;
    if (data.notes !== undefined) p[FMO.notes] = data.notes;
    if (data.incidentId !== undefined) p[FMO.incidentId] = data.incidentId;
    if (FMO.directoryOrgId && data.directoryOrgId !== undefined) p[FMO.directoryOrgId] = data.directoryOrgId;
    try { await updateRecord(TABLES.msOrganizations, id, p); await get().fetchOrganizations(); } catch (e) { console.error('[MS]', e); }
  },

  // V1 compat aliases
  // Load ALL orgs (no incident filter) for use as a lookup table in Contacts, dropdowns, etc.
  loadOrganizations: async () => {
    try {
      const r = await fetchRecords<Record<string, unknown>>(TABLES.msOrganizations, {
        limit: 500, sort: { field: 'created_at', direction: 'desc' },
      });
      set({ organizations: r.records.map(mapMSOrg) });
    } catch (e) { console.error('[MS] loadOrganizations', e); }
  },
  loadingOrgs: false,
  loadContacts: async () => { await get().fetchContacts(); },
  createContact: async (data: any) => {
    const p: Record<string,unknown> = {};
    if (data.orgId) p[FMC.orgId] = data.orgId;
    // Contacts.tsx sends `name` (full name); split or use as-is in firstName
    if (data.firstName) p[FMC.firstName] = data.firstName;
    else if (data.name) {
      const parts = String(data.name).trim().split(/\s+/);
      p[FMC.firstName] = parts.slice(0, -1).join(' ') || parts[0] || '';
      p[FMC.lastName] = parts.length > 1 ? parts[parts.length - 1] : '';
    }
    if (data.lastName) p[FMC.lastName] = data.lastName;
    if (data.title) p[FMC.title] = data.title;
    if (data.phone) p[FMC.phone] = data.phone;
    if (data.email) p[FMC.email] = data.email;
    if (data.role) p[FMC.role] = data.role;
    if (data.notes !== undefined) p[FMC.notes] = data.notes;
    if (FMC.personId && data.personId !== undefined) p[FMC.personId] = data.personId;
    try { const r = await createRecord<Record<string,unknown>>(TABLES.msContacts, p); await get().fetchContacts(); return Number(r.record_id ?? r.id ?? 0); } catch (e) { console.error('[MS]', e); return 0; }
  },
  updateContact: async (id: any, data: any) => {
    const p: Record<string,unknown> = {};
    if (data.firstName !== undefined) p[FMC.firstName] = data.firstName;
    else if (data.name !== undefined) {
      const parts = String(data.name).trim().split(/\s+/);
      p[FMC.firstName] = parts.slice(0, -1).join(' ') || parts[0] || '';
      p[FMC.lastName] = parts.length > 1 ? parts[parts.length - 1] : '';
    }
    if (data.lastName !== undefined) p[FMC.lastName] = data.lastName;
    if (data.title !== undefined) p[FMC.title] = data.title;
    if (data.phone !== undefined) p[FMC.phone] = data.phone;
    if (data.email !== undefined) p[FMC.email] = data.email;
    if (data.role !== undefined) p[FMC.role] = data.role;
    if (data.notes !== undefined) p[FMC.notes] = data.notes;
    if (FMC.personId && data.personId !== undefined) p[FMC.personId] = data.personId;
    try { await updateRecord(TABLES.msContacts, id, p); await get().fetchContacts(); } catch (e) { console.error('[MS]', e); }
  },
  deleteContact: async (id: any) => {
    try { await deleteRecord(TABLES.msContacts, id); await get().fetchContacts(); } catch (e) { console.error('[MS]', e); }
  },
  loadFacilities: async () => {
    set({ loadingFacilities: true });
    try {
      const r = await fetchRecords<any>(TABLES.msFacilities, { limit: 500, sort: { field: 'created_at', direction: 'desc' } });
      const seen = new Set<string>();
      const facilities = (r.records ?? [])
        .map((row: any): MSFacility => ({
          id: row.record_id,
          orgId: String(row[FMF.orgId] || ''),
          name: (row[FMF.facilityName] as string) || '',
          type: ((row[FMF.facilityType] as string) || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          address: (row[FMF.address] as string) || '',
          city: (row[FMF.city] as string) || '',
          state: (row[FMF.state] as string) || '',
          zip: '', buildingType: '', sqft: '', stories: '',
          occDay: '', occNight: '', hasGen: 'false', genDetails: '',
          hasHazmat: 'false', hazmatDetails: '', coordinates: '',
          notes: (row[FMF.notes] as string) || '',
          status: (row[FMF.status] as string) || 'active',
          latitude: row[FMF.latitude] ? Number(row[FMF.latitude]) : null,
          longitude: row[FMF.longitude] ? Number(row[FMF.longitude]) : null,
        }))
        .filter(f => {
          // Deduplicate: V2 migration may have created duplicate records
          const key = `${f.orgId}|${f.name.trim().toLowerCase()}|${f.address.trim().toLowerCase()}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      set({ facilities });
    } catch (e) { console.error('[MainStreet]', e); }
    finally { set({ loadingFacilities: false }); }
  },
  createFacility: async (data: Partial<MSFacility>) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.name) p[FMF.facilityName] = data.name;
      if (data.type) p[FMF.facilityType] = data.type;
      if (data.orgId) p[FMF.orgId] = data.orgId;
      if (data.address) p[FMF.address] = data.address;
      if (data.city) p[FMF.city] = data.city;
      if (data.state) p[FMF.state] = data.state;
      if (data.notes) p[FMF.notes] = data.notes;
      p[FMF.status] = data.status || 'active';
      const r = await createRecord<any>(TABLES.msFacilities, p);
      await get().loadFacilities();
      return r.record_id as number;
    } catch (e) { console.error('[MainStreet]', e); return 0; }
  },
  updateFacility: async (id: number, data: Partial<MSFacility>) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.name !== undefined) p[FMF.facilityName] = data.name;
      if (data.type !== undefined) p[FMF.facilityType] = data.type;
      if (data.orgId !== undefined) p[FMF.orgId] = data.orgId;
      if (data.address !== undefined) p[FMF.address] = data.address;
      if (data.city !== undefined) p[FMF.city] = data.city;
      if (data.state !== undefined) p[FMF.state] = data.state;
      if (data.notes !== undefined) p[FMF.notes] = data.notes;
      if (data.status !== undefined) p[FMF.status] = data.status;
      await updateRecord(TABLES.msFacilities, id, p);
      await get().loadFacilities();
    } catch (e) { console.error('[MainStreet]', e); }
  },
  deleteFacility: async (id: number) => {
    try { await deleteRecord(TABLES.msFacilities, id); await get().loadFacilities(); }
    catch (e) { console.error('[MainStreet]', e); }
  },
  orgLists: [] as any[],
  loadOrgLists: async () => {
    set({ loadingLists: true });
    try {
      const r = await queryRecords<any>(TABLES.msOrgLists, { filters: [], limit: 200 });
      set({ orgLists: r.records.map((row: any) => ({
        id: row.record_id,
        listName: row[FML.listName] || '',
        name: row[FML.listName] || '',   // alias so pages using l.name work
        description: row[FML.description] || '',
        orgIds: row[FML.orgIds] || '',
        createdBy: row[FML.createdBy] || '',
      })) });
    } catch (e) { console.error('[MainStreet]', e); }
    finally { set({ loadingLists: false }); }
  },
  createOrgList: async (data: any) => {
    try {
      const p: any = {};
      if (data.listName) p[FML.listName] = data.listName;
      if (data.description) p[FML.description] = data.description;
      if (data.orgIds) p[FML.orgIds] = data.orgIds;
      if (data.createdBy) p[FML.createdBy] = data.createdBy;
      const r = await createRecord<any>(TABLES.msOrgLists, p);
      await get().loadOrgLists(); return r.record_id as number;
    } catch (e) { console.error('[MainStreet]', e); return 0; }
  },
  updateOrgList: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.listName !== undefined) p[FML.listName] = data.listName;
      if (data.description !== undefined) p[FML.description] = data.description;
      if (data.orgIds !== undefined) p[FML.orgIds] = data.orgIds;
      await updateRecord(TABLES.msOrgLists, id, p); await get().loadOrgLists();
    } catch (e) { console.error('[MainStreet]', e); }
  },
  deleteOrgList: async (id: any) => {
    try { await deleteRecord(TABLES.msOrgLists, id); await get().loadOrgLists(); }
    catch (e) { console.error('[MainStreet]', e); }
  },
  loadPasses: async () => { await get().fetchPasses(); },

  geocodeUnmappedOrgs: async (onProgress) => {
    const WORKER_URL = import.meta.env.DEV
      ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
      : '/api/worker';
    const unmapped = get().organizations.filter(o => !o.latitude && !o.longitude && o.address);
    let count = 0;
    for (let i = 0; i < unmapped.length; i++) {
      const org = unmapped[i];
      onProgress?.(i, unmapped.length);
      try {
        const q = encodeURIComponent(`${org.address || ''} ${org.city || ''} ${org.state || ''}`.trim());
        const resp = await fetch(`${WORKER_URL}/api/here/geocode?q=${q}&limit=1`);
        if (!resp.ok) continue;
        const data = await resp.json();
        const pos = data?.items?.[0]?.position;
        if (!pos?.lat || !pos?.lng) continue;
        await updateRecord(TABLES.msOrganizations, org.id, {
          [FMO.latitude]: String(pos.lat),
          [FMO.longitude]: String(pos.lng),
        });
        count++;
      } catch { /* skip individual failure */ }
    }
    onProgress?.(unmapped.length, unmapped.length);
    await get().fetchOrganizations();
    return count;
  },
}));
